import type { AiUsageAnalyticsResponse, AiUsageLogsListResponse } from '@/types';

import { atom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';

import { apiClient } from '@/utils/api';
import { buildApiParams } from '@/utils/helpers';
import { createPaginationParams, createTableQueryOptions } from '@/components/table';

const AI_USAGE_LOGS_BASE = '/ai-usage-logs';
const AI_USAGE_ANALYTICS_BASE = '/ai-usage/analytics';

export type AiUsageListParams = {
  page: number;
  per_page: number;
  filters: Record<string, string | number | boolean | undefined>;
  sort?: string;
  refresh?: number;
};

export const aiUsageListParamsAtom = atom<AiUsageListParams>({
  ...createPaginationParams(),
  filters: {}
});

export const aiUsageHydratedAtom = atom<boolean>(false);

export const aiUsageLogsQueryAtom = atomWithQuery<AiUsageLogsListResponse>((get) => {
  const params = get(aiUsageListParamsAtom);
  const hydrated = get(aiUsageHydratedAtom);

  return createTableQueryOptions({
    queryKey: ['ai-usage-logs', params] as const,
    queryFn: async () => {
      const apiParams = buildApiParams(
        {
          page: params.page,
          per_page: params.per_page,
          sort: params.sort
        },
        params.filters || {}
      );

      const { data } = await apiClient.get(AI_USAGE_LOGS_BASE, { params: apiParams });
      return data as AiUsageLogsListResponse;
    },
    enabled: hydrated
  });
});

export const aiUsageAnalyticsQueryAtom = atomWithQuery<AiUsageAnalyticsResponse>(() => ({
  queryKey: ['ai-usage-analytics'] as const,
  queryFn: async () => {
    const { data } = await apiClient.get(AI_USAGE_ANALYTICS_BASE);
    return data as AiUsageAnalyticsResponse;
  }
}));
